import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Text } from '~/eds/src/components/Text/Text';
import { useUuid } from '~/eds/src/hooks/useUuid';
import { CreditBalance } from '~/features/billing/CreditBalance/CreditBalance';
import { useClientId } from '~/hooks/useClientId';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

import {
  useGetCreditsTooltip,
  useGetEstimatedCreditsTooltip,
} from '../billing/hooks';
import { calculateCreditEstimate } from '../billing/utils';
import { TargetEntityDetails } from '../types/entity';

interface Props {
  modelId: Uuid;
  version: number;
  targetEntityDetails: Nullable<TargetEntityDetails>;
}
export const ConfirmResumePublishModal = ({
  modelId,
  version,
  targetEntityDetails,
}: Props) => {
  const intl = useIntl();
  const clientId = useClientId();
  const dispatch = useDispatch();
  const [id] = useUuid();
  const { toast } = useToast();

  const {
    data: modelProgress = {
      docsProcessed: 0,
      docsInScope: 0,
      publishStatus: null,
      valuesFound: 0,
      docsInScopeProcessing: false,
    },
    isLoading,
  } = api.endpoints.getPromptModelProgress.useQuery({ modelId });

  const { docsInScope, docsProcessed, docsInScopeProcessing } = modelProgress;

  const newDocsInScope = calculateCreditEstimate(docsProcessed, docsInScope);

  const [publishFieldModel, { isLoading: isPublishingFieldModel }] =
    api.endpoints.publishPromptModel.useMutation();

  const handlePublish = () => {
    publishFieldModel({
      modelId,
      version,
      publishMode: 'new_docs_in_scope',
      idempotencyKey: id,
    })
      .then(() => {
        dispatch(slices.xRay.actions.setModelAction(null));
        toast({
          message: intl.formatMessage(
            {
              id: 'ConfirmResumePublishModal.publishSuccessMessage',
              defaultMessage:
                '"{targetEntityLabel}" is active and is processing documents.',
              description:
                'Success message when model is published and processing documents. Example: targetEntityLabel = "Contract Value Model"',
            },
            {
              targetEntityLabel: targetEntityDetails?.label ?? '–',
            },
          ),
          status: 'success',
        });
      })
      .catch(() => {
        toast({
          message: intl.formatMessage({
            id: 'ConfirmResumePublishModal.publishErrorMessage',
            defaultMessage: 'Failed to publish the model. Please try again.',
            description: 'Error message when model publishing fails',
          }),
          status: 'danger',
        });
      });
  };

  const creditSummary = useSelector(slices.xRay.selectors.selectCreditsSummary);

  const insufficientCredits =
    creditSummary && creditSummary.credits < newDocsInScope;

  const modalProps = {
    title: intl.formatMessage({
      id: 'ConfirmResumePublishModal.title',
      defaultMessage: 'Confirm to Publish',
      description: 'Modal title for confirming model publishing',
    }),
    children: (
      <Layout direction="column" spacing={4}>
        <Text>
          <FormattedMessage
            id="ConfirmResumePublishModal.publishingMessage"
            defaultMessage="Publishing this model will consume the estimated credits shown below. Please confirm to proceed."
            description="Message explaining credit consumption when publishing model"
          />
        </Text>
        <CreditBalance
          clientId={clientId}
          estimateUnit={{
            modelId,
            isLoadingUnitValue: isLoading || docsInScopeProcessing,
            unit: 'document-count',
            value: newDocsInScope,
          }}
          getCreditsTooltip={useGetCreditsTooltip}
          getEstimatedCreditsTooltip={useGetEstimatedCreditsTooltip}
        />
      </Layout>
    ),
    isVisible: true,
    primaryAction: {
      disabled:
        isLoading || docsInScopeProcessing || Boolean(insufficientCredits),
      tooltip:
        isLoading || docsInScopeProcessing
          ? intl.formatMessage({
              id: 'ConfirmResumePublishModal.estimatingCredits',
              defaultMessage: 'Estimating credits…',
              description: 'Tooltip when estimating credits',
            })
          : insufficientCredits
            ? intl.formatMessage({
                id: 'ConfirmResumePublishModal.insufficientCredits',
                defaultMessage: 'You do not have enough credits to publish.',
                description: 'Tooltip when user has insufficient credits',
              })
            : undefined,
      isLoading: isPublishingFieldModel,
      text: intl.formatMessage({
        id: 'ConfirmResumePublishModal.confirmButton',
        defaultMessage: 'Confirm',
        description: 'Confirm button text',
      }),
      onClick: handlePublish,
    },
    onCancel: () => {
      dispatch(slices.xRay.actions.setModelAction(null));
    },
  };
  return <Modal {...modalProps} />;
};
